import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useCreateApiAction } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ICreateApiActionPayload } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useTranslation } from 'react-i18next';
import { parseCurlToApiAction, type ParseCurlResult } from '../create-api/forms/utils/parse-curl';

type ImportCurlModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  toolId: string;
  projectKey: string;
  isBlocksTool?: boolean;
  baseUrl?: string;
};

type Phase = 'input' | 'review' | 'saving';

export function ImportCurlModal({
  open,
  onOpenChange,
  toolId,
  projectKey,
  isBlocksTool = false,
  baseUrl = '',
}: ImportCurlModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: createApiAction, isPending } = useCreateApiAction();

  const [phase, setPhase] = useState<Phase>('input');
  const [curlText, setCurlText] = useState('');
  const [parsed, setParsed] = useState<ParseCurlResult | null>(null);

  useEffect(() => {
    if (!open) {
      setPhase('input');
      setCurlText('');
      setParsed(null);
    }
  }, [open]);

  const handleParse = () => {
    try {
      const result = parseCurlToApiAction(curlText, { baseUrl });
      setParsed(result);
      setPhase('review');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('COULD_NOT_PARSE_CURL') || 'Could not parse cURL',
        description:
          error instanceof Error ? error.message : t('INVALID_CURL_COMMAND') || 'Invalid cURL command.',
      });
    }
  };

  const handleSave = async () => {
    if (!parsed) return;

    const payload: ICreateApiActionPayload = {
      ...parsed.payload,
      is_open_tool: isBlocksTool,
    };

    setPhase('saving');
    try {
      const response = await createApiAction({ toolId, projectKey, payload });

      if (!response.is_success) {
        throw new Error(
          response.message || response.detail || t('FAILED_TO_SAVE_API_ACTION') || 'Failed to save API action.',
        );
      }

      const actionId = response.item_id;
      if (!actionId) {
        throw new Error(
          t('API_ACTION_SAVED_NO_ID') || 'The API action was saved but no action ID was returned.',
        );
      }

      await queryClient.invalidateQueries({ queryKey: ['tools', 'detail', toolId, projectKey] });

      toast({
        variant: 'success',
        title: t('ACTION_CREATED') || 'Action created',
        description: t('CURL_IMPORTED_REVIEW') || 'cURL imported. Review the API details.',
      });
      onOpenChange(false);

      navigate(
        `/tool-details/${encodeURIComponent(toolId)}/create-api?actionId=${encodeURIComponent(actionId)}${isBlocksTool ? '&isBlocksTool=true' : ''}`,
      );
    } catch (error) {
      setPhase('review');
      toast({
        variant: 'destructive',
        title: t('IMPORT_FAILED') || 'Import failed',
        description:
          error instanceof Error ? error.message : t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
      });
    }
  };

  const parameterCount = parsed ? Object.keys(parsed.payload.input_parameters).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('IMPORT_FROM_CURL') || 'Import from cURL'}</DialogTitle>
          <DialogDescription>
            {t('IMPORT_FROM_CURL_DESCRIPTION') ||
              'Paste a cURL command. The request is saved as an API action, then opened so you can review parameters. Output fields cannot be inferred from cURL.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'input' && (
          <div className="flex flex-col gap-2 overflow-y-auto">
            <Label htmlFor="curl-command">{t('CURL_COMMAND') || 'cURL command'}</Label>
            <Textarea
              id="curl-command"
              value={curlText}
              onChange={(event) => setCurlText(event.target.value)}
              className="min-h-[280px] resize-none font-mono text-sm"
              spellCheck={false}
              placeholder={'curl -X POST "https://api.example.com/users" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"name":"Jane"}\''}
            />
          </div>
        )}

        {phase === 'review' && parsed && (
          <div className="flex flex-col gap-4 overflow-y-auto">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">{t('METHOD') || 'Method'}</p>
                <p className="text-sm font-medium">{parsed.payload.method}</p>
              </div>
              <div className="col-span-2 rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">{t('PATH') || 'Path'}</p>
                <p className="truncate text-sm font-medium" title={parsed.payload.endpoint}>
                  {parsed.payload.endpoint}
                </p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">{t('NAME') || 'Name'}</p>
                <p className="truncate text-sm font-medium" title={parsed.payload.name}>
                  {parsed.payload.name}
                </p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground">
                  {t('INPUT_PARAMETERS') || 'Input parameters'}
                </p>
                <p className="text-sm font-medium">{parameterCount}</p>
              </div>
            </div>

            {parsed.warnings.length > 0 && (
              <div className="max-h-40 overflow-auto rounded-lg border border-border p-3">
                <div className="mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs font-medium text-muted-foreground">
                    {t('CURL_FIELDS_SKIPPED', { count: parsed.warnings.length }) ||
                      `${parsed.warnings.length} unsupported field${parsed.warnings.length === 1 ? '' : 's'} skipped`}
                  </p>
                </div>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {parsed.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {phase === 'saving' && (
          <div className="flex h-[220px] flex-col items-center justify-center gap-3 text-center">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            <p className="text-sm font-medium">{t('SAVING_API_ACTION') || 'Saving API action...'}</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {phase === 'input' ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                {t('CANCEL') || 'Cancel'}
              </Button>
              <Button onClick={handleParse} disabled={!curlText.trim()}>
                {t('IMPORT') || 'Import'}
              </Button>
            </>
          ) : phase === 'review' ? (
            <>
              <Button variant="ghost" onClick={() => setPhase('input')} disabled={isPending}>
                {t('BACK') || 'Back'}
              </Button>
              <Button onClick={handleSave} disabled={isPending}>
                {t('SAVE_AND_CONTINUE') || 'Save & Continue'}
              </Button>
            </>
          ) : (
            <Button disabled>{t('SAVING') || 'Saving...'}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImportCurlModal;
